import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useMemo, useRef, useState} from 'react';
import {ReactFlowProvider} from 'reactflow';

import * as analytics from 'analytics/Analytics';
import {isInaccessibleInfo} from 'auth/RequestAccess';
import {ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {
  ProcessFlowchartPlatformFeatureType,
  ProcessSingletonTaskSchemaName,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {FLOWCHART_CONFIG_INITIAL_LOAD_STARTED_EVENT} from 'performance/processProcessesEvents';
import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {
  ProcessFlowchartErrorType,
  ProcessFlowchartObjectType,
  ProcessFlowchartValidationError,
} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import useOnMount from 'ui-library/hooks/useOnMount';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {ReactFlowLockedContext} from 'ui-library/reactflow/LockedContext';
import FlowchartSidepanelManager from 'ui-library/reactflow/sidepanels/sidepanel-manager/FlowchartSidepanelManager';

import {ProcessTaskDefinitionGraphQL} from '../../../../../graphql-fragments/processes/__generated/ProcessTaskDefinitionGraphQL';
import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {
  ProcessFlowchartConfigVersionGQL,
  ProcessFlowchartConfigVersionWithDefinitionsQueryGQL,
  ProcessFlowchartConfigWithValidSchemaGQL,
} from './processFlowchartConfigEditorConstants';
import ProcessFlowchartConfigEditorContent from './ProcessFlowchartConfigEditorContent';
import ProcessFlowchartConfigEditorHeader from './ProcessFlowchartConfigEditorHeader';
import {HeaderConfigAdditionalProps} from './ProcessFlowchartConfigEditorHeaderTypes';
import {FlowchartNode, isStaticTaskSchemaNodeData} from './processFlowchartConfigEditorNodeTypes';
import {FlowchartConfigReduxStateManagerWrapper} from './redux/FlowchartConfigReduxStateManagerWrapper';
import {SingletonTaskSchemasContext} from './SingletonTaskSchemasContext';
import TransitionsManager from './types/transition/TransitionsManager';
import {ProcessTaskDefinitionsResult, useAllSchemasByIdWithSingletonSchemas} from './useAllSchemasById';
import useFlowchartAutosave from './useFlowchartAutosave';
import useFlowchartConfigData from './useFlowchartConfigData';
import {FlowchartConfigStateManagerChildrenProps} from './useFlowchartState/BaseFlowchartConfigStateManager';
import {calculateSaveState} from './useFlowchartState/calculateSaveState';

export enum FlowchartConfigTab {
  SCHEMA_MAPPER_CONFIGS = 'SCHEMA_MAPPER_CONFIGS',
  TEMPLATE_FLOWCHART = 'TEMPLATE_FLOWCHART',
}

function isFlowchartConfigVersionWithDefinitions(
  flowchartConfigVersion: ProcessFlowchartConfigVersionGQL
): flowchartConfigVersion is ProcessFlowchartConfigVersionWithDefinitionsQueryGQL {
  return Object.hasOwn(flowchartConfigVersion, 'availableTaskDefinitions');
}

export interface ProcessFlowchartConfigEditorControllerProps {
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;
  flowchartConfig: ProcessFlowchartConfigWithValidSchemaGQL;
  flowchartConfigVersion: ProcessFlowchartConfigVersionGQL | null;
  locked: boolean;
  lockErrorMessage?: string;
  refetchFlowchartConfig?: () => void;
  ownerToRegistryMap?: Map<string, string>;
  headerConfigAdditionalProps?: HeaderConfigAdditionalProps;
  infoBannerMessages?: Array<string>;
}

/**
 * Process FlowchartConfig editor that displays the specified flowchart config version.
 *
 * Ensures queries have completed before rendering the editor.
 */
export default function ProcessFlowchartConfigEditorController(
  props: ProcessFlowchartConfigEditorControllerProps
): JSX.Element {
  const rootTaskSchema = useMemo(() => props.flowchartConfig.schema, [props.flowchartConfig.schema]);

  const needsSidepanel =
    props.flowchartEditorConfigurator.hasFeature(
      ProcessFlowchartPlatformFeatureType.SHOW_ADD_NODE_SIDEPANEL
    ) ||
    props.flowchartEditorConfigurator.hasFeature(
      ProcessFlowchartPlatformFeatureType.SHOW_FLOWCHART_TRANSITIONS_SIDEPANEL
    );

  const availableTaskSchemas = useMemo(
    () => accessibleObjs(props.flowchartConfigVersion?.availableSchemas ?? []),
    [props.flowchartConfigVersion?.availableSchemas]
  );

  const {
    schemasById: allSchemasById,
    processTaskDefinitionsResult,
    singletonTaskSchemasByName: _singletonTaskSchemasByName,
  } = useAllSchemasByIdWithSingletonSchemas({
    flowchartEditorConfigurator: props.flowchartEditorConfigurator,
    rootTaskSchema,
    skipTaskDefinitionsQuery: !needsSidepanel,
    availableTaskSchemas,
    availableTaskDefinitions:
      props.flowchartConfigVersion != null &&
      isFlowchartConfigVersionWithDefinitions(props.flowchartConfigVersion)
        ? [
            props.flowchartConfig.taskDefinition,
            ...(accessibleObjs(
              props.flowchartConfigVersion.availableTaskDefinitions
            ) as Array<ProcessTaskDefinitionGraphQL>),
          ]
        : undefined,
  });

  const singletonTaskSchemasByName = useMemo(
    () =>
      _singletonTaskSchemasByName ??
      ({} as Record<ProcessSingletonTaskSchemaName, ProcessTaskSchemaWithoutFlowchartConfigGraphQL>),
    [_singletonTaskSchemasByName]
  );

  if (allSchemasById == null) {
    return <CenteredHorizontalThrobber />;
  }

  return (
    <div
      style={{height: '100%'}}
      {...getTestProps({component: 'ProcessFlowchartConfigEditorController', element: 'root'})}
    >
      <SingletonTaskSchemasContext.Provider value={singletonTaskSchemasByName}>
        <ProcessFlowchartConfigDataTransformer
          {...props}
          rootTaskSchema={rootTaskSchema}
          processTaskDefinitionsResult={processTaskDefinitionsResult}
          singletonTaskSchemasByName={singletonTaskSchemasByName}
          allSchemasById={allSchemasById}
          availableTaskSchemas={availableTaskSchemas}
          refetchFlowchartConfig={props.refetchFlowchartConfig}
        />
      </SingletonTaskSchemasContext.Provider>
    </div>
  );
}

export interface ProcessFlowchartConfigDataTransformerProps extends ProcessFlowchartConfigEditorControllerProps {
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  processTaskDefinitionsResult: ProcessTaskDefinitionsResult;
  singletonTaskSchemasByName: Record<
    ProcessSingletonTaskSchemaName,
    ProcessTaskSchemaWithoutFlowchartConfigGraphQL
  >;
  allSchemasById: SchemaById;
  availableTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
}

/**
 * Converts GraphQL flowchart shape to a static representation of nodes and edges to pass to the flowchart editor.
 */
function ProcessFlowchartConfigDataTransformer(
  props: ProcessFlowchartConfigDataTransformerProps
): JSX.Element {
  const [activeTab, setActiveTab] = useState<FlowchartConfigTab>(FlowchartConfigTab.TEMPLATE_FLOWCHART);

  const {
    nodesStaticData,
    edgesStaticData,
    initialTransitions,
    didInitialConfigHaveConditionalMapping,
    didInitialFlowchartHaveChainedRouters,
    initialCidToIdMap,
    schemaMapperPtdvIdToTaskSchemaId,
    availableTaskSchemas,
    taskDefinitionsResult,
  } = useFlowchartConfigData({
    flowchartConfigVersion: props.flowchartConfigVersion,
    rootTaskSchema: props.rootTaskSchema,
    flowchartConfig: props.flowchartConfig,
    availableTaskSchemas: props.availableTaskSchemas,
    singletonTaskSchemasByName: props.singletonTaskSchemasByName,
    flowchartEditorConfigurator: props.flowchartEditorConfigurator,
    processTaskDefinitionsResult: props.processTaskDefinitionsResult,
  });

  return (
    <div
      style={{height: '100%'}}
      {...getTestProps({component: 'ProcessFlowchartConfigDataTransformer', element: 'root'})}
    >
      <FlowchartConfigReduxStateManagerWrapper
        flowchartConfigId={props.flowchartConfig.id}
        persistedFlowchartConfig={{
          nodesStaticData,
          edgesStaticData,
        }}
        flowchartConfigMetadata={{
          rootTaskSchema: props.rootTaskSchema,
          transitions: initialTransitions,
          interactionState: {
            selectedNodeIds: new Set([]),
            selectedRowIds: new Set([]),
            selectedEdgeIds: new Set([]),
            edgeLabelsByEdgeId: {},
          },
          currentVersionId:
            props.flowchartConfig.flowchartConfigVersion != null &&
            !isInaccessible(props.flowchartConfig.flowchartConfigVersion)
              ? props.flowchartConfig.flowchartConfigVersion.id
              : null,
          didInitialFlowchartHaveChainedRouters,
          flowchartTypeName: props.flowchartEditorConfigurator.flowchartTypeName,
          options: {
            isFlowchartModifiableOnCreation:
              props.flowchartConfigVersion?.isFlowchartModifiableOnCreation ?? true,
            isFlowchartModifiableAfterCreation:
              props.flowchartConfigVersion?.isFlowchartModifiableAfterCreation ?? false,
          },
        }}
      >
        {(flowchartConfigReduxData) => (
          <PresentationalProcessFlowchartConfigEditor
            {...props}
            taskDefinitionsResult={taskDefinitionsResult}
            didInitialConfigHaveConditionalMapping={didInitialConfigHaveConditionalMapping}
            didInitialFlowchartHaveChainedRouters={didInitialFlowchartHaveChainedRouters}
            availableTaskSchemas={availableTaskSchemas}
            activeTab={activeTab}
            locked={props.locked}
            setActiveTab={setActiveTab}
            cidToIdMap={initialCidToIdMap}
            schemaMapperPtdvIdToTaskSchemaId={schemaMapperPtdvIdToTaskSchemaId}
            {...flowchartConfigReduxData}
          />
        )}
      </FlowchartConfigReduxStateManagerWrapper>
    </div>
  );
}

export type PresentationalProcessFlowchartConfigEditorProps = ProcessFlowchartConfigDataTransformerProps &
  FlowchartConfigStateManagerChildrenProps & {
    taskDefinitionsResult: ProcessTaskDefinitionsResult;
    didInitialConfigHaveConditionalMapping: boolean;
    didInitialFlowchartHaveChainedRouters: boolean;
    availableTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
    cidToIdMap: CidIdMap;
    activeTab: FlowchartConfigTab;
    setActiveTab: (tab: FlowchartConfigTab) => void;
    schemaMapperPtdvIdToTaskSchemaId: Record<string, string>;
  };

/**
 * Presentational component that renders the flowchart editor page with tabs and handles save logic.
 * Contains the UI layout and event handlers but delegates state management to redux props.
 */
function PresentationalProcessFlowchartConfigEditor(
  props: PresentationalProcessFlowchartConfigEditorProps
): JSX.Element {
  const {draftFlowchartConfigAndMetadata, lastSaveError} = props;

  const {
    computedLocked,
    computedLockErrorMessage,
    isFlowchartConfigDirty,
    isFlowchartConfigSaveInFlight,
    isFlowchartConfigAutosaverPaused,
    baseValidationErrors,
    plugin,
    setDraftFlowchartConfigAndMetadata,
    triggerValidation,
  } = useFlowchartAutosave({
    flowchartConfigId: props.flowchartConfig.id,
    flowchartEditorConfiguratorFlowchartTypeName: props.flowchartEditorConfigurator.flowchartTypeName,
    rootTaskSchema: props.rootTaskSchema,
    persistedFlowchartConfig: props.persistedFlowchartConfig,
    flowchartConfigMetadata: props.flowchartConfigMetadata,
    taskDefinitionsResult: props.taskDefinitionsResult,
    didInitialFlowchartHaveChainedRouters: props.didInitialFlowchartHaveChainedRouters,
    locked: props.locked,
    lockErrorMessage: props.lockErrorMessage,
    liveStateChanges: props.liveStateChanges,
    draftFlowchartConfigAndMetadata: props.draftFlowchartConfigAndMetadata,
    draftFlowchartConfigAndMetadataRef: props.draftFlowchartConfigAndMetadataRef,
    setFlowchartConfigAndMetadata: props.setFlowchartConfigAndMetadata,
  });

  const getNodeByIdRef = useRef<(nodeId: string) => FlowchartNode | null>(() => null);
  const getNodeNameById = useCallback(
    (nodeId: string): string | null => getNodeByIdRef.current(nodeId)?.data.nodeName ?? null,
    []
  );

  const taskSchemaIdsInFlowchart = useMemo(
    () =>
      new Set<string>(
        draftFlowchartConfigAndMetadata.staticFlowchart.nodesStaticData
          .filter((node) => isStaticTaskSchemaNodeData(node))
          .map((node) => node.taskSchemaId)
      ),
    [draftFlowchartConfigAndMetadata.staticFlowchart.nodesStaticData]
  );

  const archivedSchemas =
    props.flowchartConfigVersion?.availableSchemas.filter(
      (schema) =>
        !isInaccessibleInfo(schema) && schema.archiveRecord != null && taskSchemaIdsInFlowchart.has(schema.id)
    ) ?? [];

  const archivedSchemaNames = archivedSchemas.map((s) => (isInaccessibleInfo(s) ? '(Inaccessible)' : s.name));

  const validationErrors = useMemo(() => {
    if (archivedSchemas.length > 0) {
      const archivedSchemaError: ProcessFlowchartValidationError = {
        object: {
          clientId: props.flowchartConfig.id,
          type: ProcessFlowchartObjectType.FLOWCHART_CONFIG,
        },
        errorMessage: `This flowchart uses one or more archived task schemas. Users can't create this flowchart until you replace or unarchive the following task schema(s): ${archivedSchemaNames.join(', ')}`,
        errorType: ProcessFlowchartErrorType.UNKNOWN,
        blocksPersistence: false,
      };
      return [...baseValidationErrors, archivedSchemaError];
    }
    // If we start to add multiple "base validation errors" here we should investigate
    // just moving these validations into our core validation logic
    return baseValidationErrors;
  }, [baseValidationErrors, archivedSchemas.length, archivedSchemaNames, props.flowchartConfig.id]);

  const [isInitialMount, setIsInitialMount] = useState(true);

  useOnMount(() => {
    setIsInitialMount(false);
    analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.OPENED_HORIZONTAL_FLOWCHART_EDITOR, {
      actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
      flowchartType: props.flowchartEditorConfigurator.flowchartTypeName,
      flowchartConfigId: props.flowchartConfig.id,
    });
    logProfilerTimingEvent(FLOWCHART_CONFIG_INITIAL_LOAD_STARTED_EVENT);
  });

  const {saveState, customSaveStatusMessage} = useMemo(
    () =>
      calculateSaveState({
        isSaveInFlight: isFlowchartConfigSaveInFlight,
        isDirty: isFlowchartConfigDirty,
        validationErrors,
        lastSaveError,
        isInitialMount,
      }),
    [isFlowchartConfigSaveInFlight, isFlowchartConfigDirty, validationErrors, isInitialMount, lastSaveError]
  );

  const headerConfig = plugin?.headerConfig;

  return (
    <div
      className={css(styles.rootContainer)}
      {...getTestProps({component: 'PresentationalProcessFlowchartConfigEditor', element: 'root'})}
      data-save-state={saveState}
    >
      {headerConfig && (
        // Use the Apollo-backed props.rootTaskSchema so the header reflects task schema
        // edits (e.g. renames) from the flowchart settings tab. The redux-backed copy is
        // seeded once on mount and does not update on task schema edits.
        <ProcessFlowchartConfigEditorHeader
          headerConfig={headerConfig}
          flowchartConfig={props.flowchartConfig}
          rootTaskSchema={props.rootTaskSchema}
          saveState={saveState}
          hasUnsavedChanges={isFlowchartConfigDirty || isFlowchartConfigSaveInFlight}
          isAutosaverPaused={isFlowchartConfigAutosaverPaused}
          customSaveStatusMessage={customSaveStatusMessage}
          locked={computedLocked}
          lockErrorMessage={computedLockErrorMessage}
          validationErrors={validationErrors}
          headerConfigAdditionalProps={props.headerConfigAdditionalProps}
          staticFlowchart={draftFlowchartConfigAndMetadata.staticFlowchart}
        />
      )}
      <div className={classNames('u-flex-grow', css(styles.tabContainer))}>
        <FlowchartSidepanelManager
          getNodeNameById={getNodeNameById}
          flowchartType={props.flowchartEditorConfigurator.flowchartTypeName}
        >
          <ReactFlowLockedContext.Provider value={computedLocked}>
            <ReactFlowProvider>
              <TransitionsManager draftFlowchartConfigAndMetadata={draftFlowchartConfigAndMetadata}>
                <ProcessFlowchartConfigEditorContent
                  getNodeByIdRef={getNodeByIdRef}
                  flowchart={draftFlowchartConfigAndMetadata.staticFlowchart}
                  flowchartEditorConfigurator={props.flowchartEditorConfigurator}
                  editorMode={FlowchartEditorMode.MAIN_CANVAS}
                  allSchemasById={props.allSchemasById}
                  rootTaskSchema={draftFlowchartConfigAndMetadata.metadata.rootTaskSchema!}
                  flowchartTaskDefinitionId={props.flowchartConfig.taskDefinition.id}
                  flowchartConfigId={props.flowchartConfig.id}
                  taskDefinitionsResult={props.taskDefinitionsResult}
                  didInitialConfigHaveConditionalMapping={props.didInitialConfigHaveConditionalMapping}
                  didInitialFlowchartHaveChainedRouters={props.didInitialFlowchartHaveChainedRouters}
                  refetchFlowchartConfig={props.refetchFlowchartConfig}
                  liveStateChanges={props.liveStateChanges}
                  locked={computedLocked}
                  lockErrorMessage={computedLockErrorMessage}
                  validationErrors={validationErrors}
                  draftFlowchartConfigAndMetadata={draftFlowchartConfigAndMetadata}
                  setDraftFlowchartConfigAndMetadata={setDraftFlowchartConfigAndMetadata}
                  triggerValidation={triggerValidation}
                  ownerToRegistryMap={props.ownerToRegistryMap}
                  infoBannerMessages={props.infoBannerMessages}
                  schemaMapperPtdvIdToTaskSchemaId={props.schemaMapperPtdvIdToTaskSchemaId}
                />
              </TransitionsManager>
            </ReactFlowProvider>
          </ReactFlowLockedContext.Provider>
        </FlowchartSidepanelManager>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  rootContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  divider: {
    height: '1px',
    width: '100%',
    marginTop: '30px',
    left: '0px',
    background: `${Color.GRAY_200}`,
    position: 'absolute',
  },
  tabContainer: {
    padding: 0,
    flex: '1 1 auto',
    minHeight: 0,
  },
  selectItemContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});
